import React, { useState, useMemo } from "react";
import { ArrowRightLeft, Check, Info } from "lucide-react";
import { calcolaImpostaDovuta } from "../../utils/fiscalEngine";

// Converte stringa italiana in numero (coerente con OnboardingWizard).
function italianToNumber(str) {
  if (str == null) return 0;
  if (typeof str === "number") return str;
  let cleaned = String(str).replace(/\s/g, "");
  if (cleaned.includes(".") && cleaned.includes(",")) {
    cleaned = cleaned.replace(/\./g, "").replace(",", ".");
  } else if (cleaned.includes(",")) {
    cleaned = cleaned.replace(",", ".");
  }
  return parseFloat(cleaned) || 0;
}

function formatCurrency(value) {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value) || 0);
}

// Pre-compila un input numerico in formato italiano (punto decimale → virgola).
function numberToInput(n) {
  if (!n) return "";
  return String(n).replace(".", ",");
}

/**
 * CarryOverModal — conferma di inizio anno (carry-over 1° gennaio).
 * Mostra i dati definitivi dell'anno appena chiuso (targetYear) per consolidarli
 * in fiscalByYear[targetYear]: incassi, contributi INPS versati (deducibili),
 * acconti versati manualmente e imposta dovuta derivata.
 */
export default function CarryOverModal({
  darkMode = false,
  targetYear,
  anniAttivita,
  aliquota,
  profitabilityCoefficient,
  defaultIncassi = 0,
  defaultContributi = 0,
  defaultAcconti = 0,
  incassiFromInvoices = false,
  onConfirm,
  onSkip,
}) {
  const [incassiInput, setIncassiInput] = useState(numberToInput(defaultIncassi));
  const [contributiInput, setContributiInput] = useState(numberToInput(defaultContributi));
  const [accontiInput, setAccontiInput] = useState(numberToInput(defaultAcconti));
  const [busy, setBusy] = useState(false);

  const incassi = italianToNumber(incassiInput);
  const contributiInpsVersati = italianToNumber(contributiInput);
  const accontiVersatiManuali = italianToNumber(accontiInput);

  // Imposta dovuta: imponibile × aliquota − contributi versati nell'anno (deducibili).
  const impostaDovuta = useMemo(
    () => calcolaImpostaDovuta(incassi, profitabilityCoefficient, aliquota / 100, contributiInpsVersati),
    [incassi, profitabilityCoefficient, aliquota, contributiInpsVersati]
  );

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm({ incassi, contributiInpsVersati, accontiVersatiManuali, impostaDovuta });
    } finally {
      setBusy(false);
    }
  };

  const handleSkip = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onSkip();
    } finally {
      setBusy(false);
    }
  };

  const inputClass = `w-full pl-10 pr-4 py-3 rounded-xl border-2 text-sm focus:outline-none focus:ring-2 focus:ring-forfy-500 ${
    darkMode ? "border-neutral-700 bg-neutral-800 text-white" : "border-neutral-300 bg-white text-neutral-900"
  }`;
  const labelClass = `block text-sm font-medium mb-1 ${darkMode ? "text-neutral-300" : "text-neutral-700"}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: darkMode ? "rgba(0,0,0,0.85)" : "rgba(0,0,0,0.5)" }}
    >
      <div className={`w-full max-w-lg rounded-2xl shadow-2xl border overflow-hidden ${
        darkMode ? "bg-neutral-900 border-neutral-800" : "bg-white border-neutral-200"
      }`}>
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-2">
            <ArrowRightLeft className={`w-5 h-5 ${darkMode ? "text-forfy-400" : "text-forfy-600"}`} />
            <h2 className={`text-lg font-bold ${darkMode ? "text-white" : "text-neutral-900"}`}>
              Chiusura anno {targetYear}
            </h2>
          </div>
          <p className={`text-sm mt-1 ${darkMode ? "text-neutral-400" : "text-neutral-600"}`}>
            Conferma i dati definitivi del {targetYear}: servono a calcolare saldo e acconti del {targetYear + 1}.
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {incassiFromInvoices && (
            <div className={`flex items-start gap-2 p-3 rounded-xl text-xs ${
              darkMode ? "bg-forfy-900/30 text-forfy-200" : "bg-forfy-50 text-forfy-700"
            }`}>
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Incassi pre-compilati dalla somma delle fatture incassate {targetYear}. Verifica e correggi se necessario.</span>
            </div>
          )}

          <div>
            <label className={labelClass}>Incassi totali {targetYear}</label>
            <div className="relative">
              <span className={`absolute left-4 top-1/2 -translate-y-1/2 text-sm ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>€</span>
              <input
                type="text"
                inputMode="decimal"
                value={incassiInput}
                onChange={(e) => setIncassiInput(e.target.value.replace(/[^0-9,.]/g, ""))}
                placeholder="0,00"
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Contributi INPS versati nel {targetYear} (solo sezione INPS)</label>
            <div className="relative">
              <span className={`absolute left-4 top-1/2 -translate-y-1/2 text-sm ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>€</span>
              <input
                type="text"
                inputMode="decimal"
                value={contributiInput}
                onChange={(e) => setContributiInput(e.target.value.replace(/[^0-9,.]/g, ""))}
                placeholder="0,00"
                className={inputClass}
              />
            </div>
            <p className={`text-xs mt-1 ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
              Solo la parte INPS dei tuoi F24, dedotta dall'imponibile per il calcolo
              dell'imposta sostitutiva. La sezione Erario va nel campo qui sotto.
            </p>
          </div>

          <div>
            <label className={labelClass}>Acconti imposta sostitutiva versati nel {targetYear} (solo sezione Erario)</label>
            <div className="relative">
              <span className={`absolute left-4 top-1/2 -translate-y-1/2 text-sm ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>€</span>
              <input
                type="text"
                inputMode="decimal"
                value={accontiInput}
                onChange={(e) => setAccontiInput(e.target.value.replace(/[^0-9,.]/g, ""))}
                placeholder="0,00"
                className={inputClass}
              />
            </div>
            <p className={`text-xs mt-1 ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
              Usati solo se superiori agli acconti rilevati dagli F24 registrati: non si sommano,
              così lo stesso versamento non viene contato due volte.
            </p>
          </div>

          {/* Anteprima imposta dovuta */}
          <div className={`p-4 rounded-xl ${darkMode ? "bg-neutral-800" : "bg-neutral-50"}`}>
            <div className="flex items-center justify-between text-sm">
              <span className={darkMode ? "text-neutral-300" : "text-neutral-700"}>
                Imposta dovuta {targetYear} ({aliquota}% · {anniAttivita}° anno)
              </span>
              <span className={`font-bold ${darkMode ? "text-amber-400" : "text-amber-600"}`}>
                {formatCurrency(impostaDovuta)}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 flex items-center justify-between border-t ${
          darkMode ? "border-neutral-800" : "border-neutral-200"
        }`}>
          <button
            type="button"
            onClick={handleSkip}
            disabled={busy}
            className={`px-3 py-2 rounded-lg text-sm disabled:opacity-50 ${
              darkMode ? "text-neutral-400 hover:bg-neutral-800" : "text-neutral-600 hover:bg-neutral-100"
            }`}
          >
            Salta
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy}
            className="flex items-center gap-1 px-5 py-2.5 rounded-lg text-sm font-medium bg-forfy-500 text-white hover:bg-forfy-600 disabled:opacity-50"
          >
            Conferma e salva
            <Check className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
